import React, { useMemo, useState } from "react";
import InfoPage from "../InfoPage";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { uploadFile, validateFile } from "../../utils/fileUpload";
import IntroVideoUpload from "../../components/IntroVideoUpload";
import ShowcaseUpload from "../../components/ShowcaseUpload";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Icon from "../../components/Icon";
import './CreatorOnboarding.sass';

const CATEGORIES = [
  { key: "travel", label: "Travel" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "parenting_family", label: "Parenting/Family" },
  { key: "food", label: "Food" },
  { key: "fashion_beauty", label: "Fashion/Beauty" },
  { key: "health_fitness", label: "Health & Fitness" },
  { key: "professional_photographer", label: "Professional Photographer" },
  { key: "drone_footage", label: "Drone Footage" },
  { key: "other", label: "Other" },
];

const PREFS = [
  { key: "paid_only", label: "Accepting Paid Partnerships Only" },
  { key: "gifted_and_paid", label: "Accepting Gifted & Paid Partnerships" },
  { key: "require_pet_friendly", label: "Pet Parent" },
  { key: "flexible_last_minute", label: "Flexible and willing to travel last minute" },
  { key: "require_advanced_notice", label: "Require advanced notice to travel" },
  { key: "willing_weekdays", label: "Willing to travel/stay on weekdays" },
  { key: "weekends_only", label: "Available on weekends only" },
];

const inputStyle = {
  border: 'none',
  borderBottom: '2px solid rgba(0,0,0,.12)',
  background: 'transparent',
  padding: '8px 0',
  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
  fontSize: '16px',
  color: '#0f084b',
  outline: 'none',
  width: '100%'
};

const labelStyle = {
  fontFamily: "'Sailors', serif",
  fontSize: '16px',
  color: '#0f084b',
  textAlign: 'left',
  marginTop: 6
};

const Chip = ({ text, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 10px', borderRadius: 16, background: '#f0f0f0',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b'
  }}>
    {text}
    <button type="button" onClick={onRemove} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>✕</button>
  </span>
);

const CreatorOnboarding = () => {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const defaultFirstName = params.get('firstName') || '';
  const defaultEmail = params.get('email') || '';

  const [firstName] = useState(defaultFirstName);
  const [email] = useState(defaultEmail);

  const [handle, setHandle] = useState("");
  const [locationCountry, setLocationCountry] = useState("");
  const [locationState, setLocationState] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [gender, setGender] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [youtube, setYoutube] = useState("");
  const [otherUrl, setOtherUrl] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);
  const [nicheOther, setNicheOther] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnerInput, setPartnerInput] = useState("");
  const [prefs, setPrefs] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [introVideo, setIntroVideo] = useState(null);
  const [showcaseAssets, setShowcaseAssets] = useState([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUploadSuccess, setAvatarUploadSuccess] = useState(false);
  const [mediaKitUrl, setMediaKitUrl] = useState("");
  const [mediaKitFile, setMediaKitFile] = useState(null);
  const [mediaKitUploading, setMediaKitUploading] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle media kit file upload
  const handleMediaKitFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      alert('PDF file size must be less than 50MB');
      return;
    }
    
    setMediaKitUploading(true);
    try {
      const url = await uploadFile(file);
      setMediaKitUrl(url);
      setMediaKitFile(null);
    } catch (error) {
      alert('Failed to upload media kit PDF. Please try again.');
    } finally {
      setMediaKitUploading(false);
    }
  };

  // Handle country change - reset state and city when country changes
  const handleCountryChange = (value) => {
    setLocationCountry(value);
    if (value !== locationCountry) {
      setLocationState(""); // Reset state when country changes
      setLocationCity(""); // Reset city when country changes
    }
  };

  // Handle state change - reset city when state changes
  const handleStateChange = (value) => {
    setLocationState(value);
    if (value !== locationState) {
      setLocationCity(""); // Reset city when state changes
    }
  };

  const header = useMemo(() => (
    <>
      <h3 className="creator-onboarding__header">Hey, {firstName || 'friend'}!</h3>
      <div className="creator-onboarding__subheader">We need some more information to get started.</div>
    </>
  ), [firstName]);

  const handleCatToggle = (key) => {
    setSelectedCats((prev) => {
      const exists = prev.includes(key);
      if (exists) {
        // Allow removing selected categories
        return prev.filter((k) => k !== key);
      } else {
        // Only allow adding if less than 3 are selected
        if (prev.length >= 3) {
          return prev; // Don't change if limit reached
        }
        return [...prev, key];
      }
    });
  };

  const handlePrefToggle = (key) => {
    setPrefs((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const addPartnerChip = () => {
    const v = partnerInput.trim();
    if (!v) return;
    setPartners((p) => [...p, v]);
    setPartnerInput("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const socials = {
        instagram: instagram || null,
        tiktok: tiktok || null,
        youtube: youtube || null,
        other: otherUrl || null,
      };

      const country = locationCountry;
      const city = locationCity;
      const state = locationState;

      const res = await fetch('/api/creator/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || null,
          lastName: null,
          handle: handle || null,
          locationCountry: country || null,
          locationCity: city || null,
          locationState: state || null,
          ageRange: ageRange || null,
          gender: gender || null,
          socials,
          categories: selectedCats,
          nicheOther: selectedCats.includes('other') ? nicheOther : null,
          partners,
          preferences: prefs,
          avatarUrl,
          introVideo,
          showcaseAssets,
          mediaKitUrl,
          paypalEmail: paypalEmail || null,
        })
      });
      if (!res.ok) throw new Error('save_failed');
      try {
        if (email) {
          localStorage.setItem('postaway_email', email);
          localStorage.setItem('postaway_user_type', 'creator');
        }
      } catch (_) {}
      const qs = new URLSearchParams({ email: email || '' }).toString();
      window.location.assign(`/success?${qs}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert('There was an error saving your profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const belowContent = (
    <>
      {/* Left side - Image Upload */}
      <div className="creator-onboarding__left">
        <IntroVideoUpload
          className="creator-onboarding__intro-video"
          value={introVideo}
          onChange={setIntroVideo}
        />
        <ShowcaseUpload
          className="creator-onboarding__showcase"
          mode="onboarding"
          value={showcaseAssets}
          onChange={setShowcaseAssets}
        />
        <p className="creator-onboarding__avatar-description">Skip for now and add one later from your dashboard.</p>

        <label htmlFor="avatar" className="creator-onboarding__avatar-label">
          {avatarUploading ? (
            <>
              <span className="creator-onboarding__avatar-uploading">
                Uploading and converting image...
              </span>
              <small style={{ color: '#666' }}>This may take up to 30 seconds for HEIC files</small>
            </>
          ) : avatarUploadSuccess ? (
            <>
              <span className="creator-onboarding__avatar-success-icon">✓</span>
              <span className="creator-onboarding__avatar-success-text">
                Avatar uploaded successfully!
              </span>
            </>
          ) : avatarUrl ? (
            <img alt="avatar" src={avatarUrl} className="creator-onboarding__avatar-img" />
          ) : avatar ? (
            <img alt="avatar" src={URL.createObjectURL(avatar)} className="creator-onboarding__avatar-img" />
          ) : (
            <div className="creator-onboarding__avatar-text">Upload your Profile Picture (jpeg or png)</div>
          )}
        </label>
        <input id="avatar" type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }} onChange={async (e) => {
          const file = e.target.files?.[0] || null;
          
          if (file) {
            setAvatar(file);
            setAvatarUploading(true);
            setAvatarUploadSuccess(false);
            
            try {
              // Upload file directly (handles HEIC conversion)
              const uploadedUrl = await uploadFile(file);
              setAvatarUrl(uploadedUrl);
              setAvatar(null); // Clear file after successful upload
              setAvatarUploadSuccess(true);
              
              // Hide checkmark after 2 seconds
              setTimeout(() => setAvatarUploadSuccess(false), 2000);
            } catch (error) {
              console.error('Upload failed:', error);
              alert('Failed to upload image. Please try again.');
              setAvatar(null);
              setAvatarUrl(null);
            } finally {
              setAvatarUploading(false);
            }
          } else {
            setAvatar(null);
            setAvatarUrl(null);
          }
        }} />
        <div className="creator-onboarding__avatar-hint">
          Maximum file size: 4MB
        </div>
        <p className="creator-onboarding__avatar-description">Upload your Profile Picture here (jpeg or png)!</p>
      </div>

      {/* Right side - Form */}
      <div className="creator-onboarding__right">
        <form onSubmit={handleSave} className="creator-onboarding__form">
          {header}

          <div className="creator-onboarding__row">
            <div className="creator-onboarding__field">
              <LocationAutocomplete
                type="country"
                value={locationCountry}
                onChange={handleCountryChange}
                placeholder="Start typing country..."
                label="Country"
              />
            </div>
          </div>

          <div className="creator-onboarding__row">
            <div className="creator-onboarding__field">
              <LocationAutocomplete
                type="state"
                value={locationState}
                onChange={handleStateChange}
                placeholder={locationCountry === "United States" ? "Start typing state..." : "State (optional)"}
                label="State"
                disabled={locationCountry && locationCountry !== "United States"}
              />
            </div>
            <div className="creator-onboarding__field">
              <LocationAutocomplete
                type="city"
                value={locationCity}
                onChange={setLocationCity}
                stateValue={locationState}
                placeholder={locationState ? "Start typing city..." : locationCountry === "United States" ? "Select state first" : "Start typing city..."}
                label="City"
                disabled={locationCountry === "United States" && !locationState}
              />
            </div>
          </div>

          <div className="creator-onboarding__row">
            <div className="creator-onboarding__field">
              <select 
                className="creator-onboarding__select"
                value={ageRange} 
                onChange={(e) => setAgeRange(e.target.value)}
              >
                <option value="">Select age range</option>
                <option value="18-25">18-25</option>
                <option value="26-36">26-36</option>
                <option value="37-50">37-50</option>
                <option value="51-65">51-65</option>
                <option value="66+">66 and above</option>
              </select>
              <label className="creator-onboarding__label">Age Range</label>
            </div>
            <div className="creator-onboarding__field">
              <select 
                className="creator-onboarding__select"
                value={gender} 
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
              <label className="creator-onboarding__label">Gender</label>
            </div>
          </div>

          <div>
            <div className="creator-onboarding__input-wrapper">
              <span className="creator-onboarding__input-prefix">@</span>
              <input 
                className="creator-onboarding__input creator-onboarding__input-with-prefix"
                value={handle} 
                onChange={(e) => {
                  // Remove @ sign if user types it
                  const value = e.target.value.replace('@', '');
                  setHandle(value);
                }} 
                placeholder="yourhandle" 
              />
            </div>
            <label className="creator-onboarding__label">Handle (Display Name)</label>
          </div>

          <div>
            <input className="creator-onboarding__input" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="" />
            <label className="creator-onboarding__label">Instagram URL</label>
          </div>
          <div>
            <input className="creator-onboarding__input" value={tiktok} onChange={(e) => setTiktok(e.target.value)} placeholder="" />
            <label className="creator-onboarding__label">TikTok URL</label>
          </div>
          <div>
            <input className="creator-onboarding__input" value={youtube} onChange={(e) => setYoutube(e.target.value)} placeholder="" />
            <label className="creator-onboarding__label">YouTube URL</label>
          </div>
          <div>
            <input className="creator-onboarding__input" value={otherUrl} onChange={(e) => setOtherUrl(e.target.value)} placeholder="" />
            <label className="creator-onboarding__label">Other URL</label>
          </div>

          {/* Categories */}
          <div>
            <div className="creator-onboarding__categories">
              {CATEGORIES.map((c) => {
                const isSelected = selectedCats.includes(c.key);
                const isDisabled = !isSelected && selectedCats.length >= 3;
                return (
                  <button 
                    type="button" 
                    key={c.key} 
                    onClick={() => handleCatToggle(c.key)}
                    disabled={isDisabled}
                    className={`creator-onboarding__category-button ${isSelected ? 'selected' : ''}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            {selectedCats.includes('other') && (
              <div style={{ marginTop: 12 }}>
                <input 
                  className="creator-onboarding__input" 
                  value={nicheOther} 
                  onChange={(e) => setNicheOther(e.target.value)} 
                  placeholder="Please specify..." 
                />
              </div>
            )}
            <label className="creator-onboarding__label">Categories</label>
            <div className="creator-onboarding__category-counter">
              {selectedCats.length}/3 selected
            </div>
          </div>

          {/* Partners chips */}
          <div>
            <div className="creator-onboarding__partners-chips">
              {partners.map((p, i) => (
                <Chip key={`${p}-${i}`} text={p} onRemove={() => setPartners((arr) => arr.filter((_, idx) => idx !== i))} />
              ))}
            </div>
            <div className="creator-onboarding__partner-input-row">
              <input 
                className="creator-onboarding__input creator-onboarding__partner-input" 
                value={partnerInput} 
                onChange={(e) => setPartnerInput(e.target.value)} 
                placeholder="Add partner" 
              />
              <button 
                type="button" 
                onClick={addPartnerChip} 
                className="creator-onboarding__partner-add-button"
              >
                Add
              </button>
            </div>
            <label className="creator-onboarding__label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              Previous Collaboration Partners
              <Tippy content="Enter any previous hosts, hotels or tourism boards you have collaborated with">
                <span style={{ display: 'inline-flex', cursor: 'help' }}>
                  <Icon name="info" size={14} />
                </span>
              </Tippy>
            </label>
          </div>

          {/* Media Kit - PDF Upload or URL */}
          <div>
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', marginBottom: '16px' }}>
              {/* PDF Upload Option */}
              <div>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleMediaKitFileChange}
                  disabled={mediaKitUploading}
                  style={{ display: 'none' }}
                  id="media-kit-upload-onboarding"
                />
                <label
                  htmlFor="media-kit-upload-onboarding"
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    background: '#0f084b',
                    color: 'white',
                    borderRadius: 8,
                    cursor: mediaKitUploading ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    opacity: mediaKitUploading ? 0.6 : 1
                  }}
                >
                  {mediaKitUploading ? 'Uploading...' : 'Upload PDF Media Kit'}
                </label>
                {mediaKitUrl && (mediaKitUrl.endsWith('.pdf') || mediaKitUrl.includes('blob.vercel-storage.com')) && (
                  <a
                    href={mediaKitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginLeft: '12px',
                      color: '#0f084b',
                      textDecoration: 'underline',
                      fontSize: 14
                    }}
                  >
                    View Current PDF
                  </a>
                )}
              </div>
              
              {/* OR divider */}
              <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
                OR
              </div>
              
              {/* URL Input Option */}
              <div>
                <input 
                  className="creator-onboarding__input"
                  value={mediaKitUrl} 
                  onChange={(e) => setMediaKitUrl(e.target.value)} 
                  placeholder="https://your-media-kit-link.com" 
                />
                <label className="creator-onboarding__label">Media Kit URL</label>
              </div>
            </div>
          </div>

          {/* PayPal Email for Payouts */}
          <div>
            <input 
              type="email"
              className="creator-onboarding__input"
              value={paypalEmail} 
              onChange={(e) => setPaypalEmail(e.target.value)} 
              placeholder="your-paypal@email.com" 
            />
            <label className="creator-onboarding__label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              PayPal Email (optional)
              <Tippy content="Enter your PayPal email address to receive payouts for paid collaborations">
                <span style={{ display: 'inline-flex', cursor: 'help' }}>
                  <Icon name="info" size={14} />
                </span>
              </Tippy>
            </label>
          </div>

          {/* Preferences multi-select */}
          <div>
            <div className="creator-onboarding__preferences">
              {PREFS.map((p) => (
                <button 
                  type="button" 
                  key={p.key} 
                  onClick={() => handlePrefToggle(p.key)}
                  className={`creator-onboarding__preference-button ${prefs.includes(p.key) ? 'selected' : ''}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <label className="creator-onboarding__label">Collaboration Preference</label>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting} 
            className="creator-onboarding__submit"
          >
            {isSubmitting ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </>
  );

  return <InfoPage title="Creator Onboarding" divider belowContent={belowContent} disableStamps />;
};

export default CreatorOnboarding;


